import axios from 'axios';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';
import { getUploadGenerationToken } from 'services/template.services';
import {
  dayjsWithStandardOffset,
  dayjsWithTimezone,
  getOffsetWithStandardTime,
} from 'src/app/obx/pages/schedules/helper';
import { numberToDays } from 'src/utils/constants/schedules';

import { actionItemTypeKeys, enumResponseType, placesMap } from '../utils/constants';

dayjs.extend(utc);
dayjs.extend(timezone);
export const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

/**
 *
 * @param {*} key
 * @param {*} param1
 * @returns
 */
export const removeKey = (key, { [key]: _, ...rest }) => rest;
export const isObjectEmpty = (obj) => {
  if (!obj) {
    return true;
  }
  return Object.keys(obj).length === 0;
};

export const generateUniqueId = () => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 10000);
  return `${timestamp}-${random}`;
};

export const mapNameAndLabels = (data) => {
  try {
    return data?.map((item) => {
      return { id: item?.id, value: item?.name, label: item?.name };
    });
  } catch (e) {
    throw new Error(e.message);
  }
};

export const mapFranchiseDetailData = (
  data,
  itemAreaKey = 'franchiseArea',
  zonesOrSites = 'zones',
  childrenAreaKey = 'zoneArea',
) => {
  try {
    const temp = { ...data, coordinates: data?.[itemAreaKey] };
    temp?.[zonesOrSites]?.forEach((item) => {
      item.parentId = data?.id;
      item.coordinates = item?.[childrenAreaKey];
    });
    return temp;
  } catch (e) {
    throw new Error(e.message);
  }
};

export const mapLocationInfo = (data) => {
  return {
    ...data,
    country: data?.country?.id || '',
    city: data?.city?.id || '',
    countryCode: data?.country?.countryCode || data?.countryCode || '',
    state: data?.state?.id || '',
  };
};

export const mapAddressDetails = (data) => {
  const { t } = useTranslation();

  const { address, city, state, postalCode } = data || {};
  let result = [address, city, state, postalCode].filter((value) => value).join(', ');
  return result || t('commonText.nA');
};

export const addZoneDetails = (data) => {
  try {
    return {
      ...data,
      supervisor: data?.supervisor?.[0]?.id || data?.supervisor?.id,
    };
  } catch (e) {
    throw new Error(e.message);
  }
};

export const timeFormat12h = (value) => {
  const minutes = dayjs(value).minute();
  const timeFormat = minutes === 0 ? 'ha' : 'h:mma';
  return value ? dayjs(value).format(timeFormat).replace('m', '') : '';
};

export const checkHowMuchTimeIsLeft = (time, interval = 30) => {
  const targetTime = dayjsWithStandardOffset(time).utc(); // Replace with your target time

  const now = getCurrentStandardTimeInIsoWrtTimezone().utc();

  const thirtyMinutesBefore = targetTime.subtract(interval, 'minute');

  return now.isAfter(thirtyMinutesBefore);
};

export const getDaysStringFromNumbers = (numbers = []) => {
  const allElementsMatched = (arr = []) => {
    return arr.every((item) => numbers.includes(item)) && numbers.length === arr.length;
  };

  if (numbers.length === 7) {
    return 'Everyday';
  } else if (allElementsMatched([1, 2, 3, 4, 5])) {
    return 'Weekdays';
  } else if (allElementsMatched([0, 6])) {
    return 'Weekend';
  }

  let days = numbers.reduce((acc, cur) => {
    acc = [...acc, numberToDays[cur]];

    return acc;
  }, []);
  return days.join(', ');
};

// Convert minutes to hours and minutes format
export const convertMinutesToHMFormat = (totalMinutes) => {
  if (!totalMinutes) return '0hrs';

  const hours = Math.floor(totalMinutes / 60);
  const usedMinutes = hours * 60;

  const minutes = Math.round(totalMinutes) - usedMinutes;

  const formattedTime = minutes ? `${hours}h ${minutes}m` : `${hours}hrs`;
  return formattedTime;
};

// fromValue and toValue will be in ISO format
export const setDateToTime = ({ fromValue, toValue }) => {
  const dutyStartDate = dayjs(fromValue).get('date');
  const dutyStartMonth = dayjs(fromValue).get('month');
  const dutyStartYear = dayjs(fromValue).get('year');

  const updatedValue = dayjs(toValue)
    .set('date', dutyStartDate)
    .set('month', dutyStartMonth)
    .set('year', dutyStartYear);

  return updatedValue.toISOString();
};

export const getErrorKey = (key, formDataKey, index) => {
  return `${formDataKey},${index},${key}`;
};

export const showError = ({ key, formDataKey, index, errors }) => {
  return errors?.[`${getErrorKey(key, formDataKey, index)}`];
};

export const removeDataFromObject = (array, finalPayload) => {
  array.forEach((a) => {
    const key = finalPayload[a];
    if (!key) {
      // ? NOTE: if the key "name" is not getting used add _ before it or this rule will suffice the need here.
      // eslint-disable-next-line no-unused-vars
      const { [a]: name, ...rest } = finalPayload;
      finalPayload = rest;
    }
  });

  return finalPayload;
};
/**
 * @description maps dropdown data of address entities for API integration
 * @param {*} data
 * @returns {object}
 */
export const mapDropDownData = (data) => {
  return { id: data?.id, name: data?.name, label: data?.name, value: data?.id };
};
export const mapFullName = (firstName, lastName) => {
  // Check if both firstName and lastName are null, and return "N/A" in that case
  if (firstName === null && lastName === null) {
    return 'NA';
  }

  // Handle null values for firstName and lastName separately
  const fullName = ((firstName || '') + ' ' + (lastName || '')).trim();

  return fullName === '' ? 'NA' : fullName;
};

export const transformPieChartData = (labels, values, total) => {
  if (!labels || !values || labels.length !== values.length) {
    return null;
  }

  const data = labels.map((label, index) => ({
    name: label,
    value: values[index],
  }));

  return {
    data,
    total: total || data.reduce((sum, entry) => Number(sum) + Number(entry.value), 0),
  };
};

export const transformBarChartData = (xAxis, series) => {
  if (!xAxis || !series || xAxis.length !== series.length) {
    return null;
  }

  return {
    xAxis,
    series,
  };
};

export const parseDataLabels = (months) => {
  return months.map((month) => `${month.substr(0, 3)}' ${month.split(' ')[1].substr(2)}`);
};

export const getUniqueOrderedDaysOfWeekBetweenDates = (startDate, endDate) => {
  const daysOfWeekWithVal = [
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
    { label: 'Sunday', value: 0 },
  ];
  const days = new Set(); // Using a Set to store unique values

  let currentDate = dayjs(startDate).startOf('day');

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
    const dayOfWeek = currentDate.format('dddd');
    const dayObject = daysOfWeekWithVal.find((day) => day.label === dayOfWeek);
    days.add(dayObject);
    currentDate = currentDate.add(1, 'day');
  }

  // Convert the Set to an array and sort it by the custom order of weekdays
  return Array.from(days)
    .sort((a, b) => {
      const valA = daysOfWeekWithVal.findIndex((day) => day.label === a.label);

      const valB = daysOfWeekWithVal.findIndex((day) => day.label === b.label);

      return valA - valB;
    })
    .map((a) => {
      return { ...a, label: a.label.toLowerCase() };
    });
};

export const formatDate = (date, template = 'MM/DD/YYYY') => {
  const formattedDate = dayjs(date).format(template);
  return formattedDate === 'Invalid Date' ? '' : formattedDate;
};

/**
 * @description Cook data for geoLocation
 * @param {Number} actionItemId
 * @param {Object} dataFromGeoLocation
 * @param {String} activePolygonType
 * @returns Object
 */
export const findParentAndSiblingsPolygon = (
  actionItemId,
  dataFromGeoLocation,
  activePolygonType,
  filterActiveActionItem = true,
) => {
  let [parent, error, siblings, franchiseArea, inputData, children] = [
    null,
    null,
    {},
    [],
    dataFromGeoLocation?.data,
    [],
  ];

  /**
   * Filter action item from respective array of objects
   */
  franchiseArea.franchises =
    activePolygonType === actionItemTypeKeys.franchise && filterActiveActionItem && actionItemId
      ? inputData?.franchises?.filter((franchise) => franchise?.id != actionItemId)
      : inputData?.franchises;

  franchiseArea.zones =
    activePolygonType === actionItemTypeKeys.zone && filterActiveActionItem && actionItemId
      ? inputData?.zones?.filter((zone) => zone?.id != actionItemId)
      : inputData?.zones;

  franchiseArea.sites =
    activePolygonType === actionItemTypeKeys.site && filterActiveActionItem && actionItemId
      ? inputData?.sites?.filter((site) => site?.id != actionItemId)
      : inputData?.sites;

  /**
   * find parent, children and siblings
   */
  switch (activePolygonType) {
    case actionItemTypeKeys.site:
      parent = inputData?.zones?.find((zone) => zone?.id == inputData?.parentId);
      siblings = actionItemId
        ? inputData?.sites?.filter((site) => site?.id != actionItemId)
        : inputData?.sites;
      children = [];

      break;
    case actionItemTypeKeys.zone:
      parent = inputData?.franchises?.find((franchise) => franchise?.id == inputData?.parentId);
      siblings = actionItemId
        ? inputData?.zones?.filter((zone) => zone?.id != actionItemId)
        : inputData?.zones;
      children = actionItemId ? inputData?.sites : [];

      break;
    case actionItemTypeKeys.franchise:
      siblings = actionItemId
        ? inputData?.franchises?.filter((franchise) => franchise?.id != actionItemId)
        : inputData?.franchises;
      parent = null;
      children = actionItemId ? inputData?.zones : [];

      break;
    default:
      break;
  }
  return { parent, error, siblings, franchiseArea, children };
};
export const getTimeWithInterval = ({ startHour = 1, endHour = 24, skipFirst = false }) => {
  let dates = [];

  let currentDate = dayjs().startOf('day');
  let start = currentDate.set('hour', startHour).set('minute', 0).set('second', 0);
  const end = currentDate.set('hour', endHour).set('minute', 0).set('second', 0);

  if (skipFirst) {
    start = start.add(30, 'minute');
  }

  while (start.isBefore(end) || start.isSame(end)) {
    const intervaledTime = start.format('hh:mm A');
    start = start.add(30, 'minute');

    dates = [
      ...dates,
      {
        value: intervaledTime,
        label: intervaledTime,
      },
    ];
  }

  return dates;
};

export const getTimeWithIntervalNextTwentyFourHours = ({
  startHour = 1,
  skipFirst = false,
  startMints = 0,
}) => {
  let dates = [];

  let currentDate = dayjs().startOf('day');
  let start = currentDate.set('hour', startHour).set('minute', startMints).set('second', 0);
  const end = start.add(24, 'hour'); // Set end time as 24 hours from the start

  if (skipFirst) {
    start = start.add(30, 'minute');
  }

  while (start.isBefore(end) || start.isSame(end)) {
    const intervaledTime = start.format('hh:mm A');

    dates = [
      ...dates,
      {
        value: intervaledTime,
        label: intervaledTime,
      },
    ];

    start = start.add(30, 'minute');
  }

  return dates;
};

export const clearCookies = () =>
  document.cookie
    .split(';')
    .forEach(
      (cookie) =>
        (document.cookie = cookie
          .replace(/^ +/, '')
          .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`)),
    );

export const scrollToInValidField = () => {
  setTimeout(() => {
    let element = document.querySelector('.Mui-error');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
};

export const getStartAndEndOfMonth = (date) => {
  // Ensure the input is a Date object
  const inputDate = new Date(date);

  // Set the date to the first day of the month in UTC
  const startDate = new Date(Date.UTC(inputDate.getUTCFullYear(), inputDate.getUTCMonth(), 1));

  // Set the date to the last day of the month in UTC
  const lastDay = new Date(Date.UTC(inputDate.getUTCFullYear(), inputDate.getUTCMonth() + 1, 0));
  const endDate = new Date(
    Date.UTC(
      lastDay.getUTCFullYear(),
      lastDay.getUTCMonth(),
      lastDay.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );

  // Format dates to ISO strings
  const startISOString = startDate.toISOString();
  const endISOString = endDate.toISOString();

  return { start: startISOString, end: endISOString };
};

export const listStatusHandler = (status) => {
  const statusEnum = {
    onTime: { title: 'On-time', color: 'warning' },
    lateStarted: { title: 'Late', color: 'warning' },
    present: { title: 'Present', color: 'success' },
    onLeave: { title: 'Time Off', color: 'error' },
    absent: { title: 'Absent', color: 'error' },
    dedicated: { title: 'Dedicated', color: 'primary' },
    earlyLeft: { title: 'Early Left', color: 'warning' },
    dispatch: { title: 'Dispatch', color: 'error' },
    completed: { title: 'Completed', color: 'success' },
    extra: { title: 'Extra', color: 'success' },
    escalated: { title: 'Escalated', color: 'error' },
    leaveApproved: { title: ' Approved', color: 'success' },
    missedCheckpoint: { title: 'Missed Checkpoint', color: 'error' },
    missedReport: { title: 'Missed Report', color: 'error' },
    overTime: { title: 'Over Time', color: 'primary' },
    notStarted: { title: 'Not Started', color: 'error' },
    breakStarted: { title: 'Break Started', color: 'primary' },
    breakEnded: { title: 'Break Ended', color: 'primary' },
    shiftStarted: { title: 'Shift Started', color: 'primary' },
    shiftEnded: { title: 'Shift Ended', color: 'success' },
    leaveApplied: { title: ' Applied', color: 'primary' },
    lateShiftStarted: { title: 'Late Shift Started', color: 'error' },
    earlyShiftStarted: { title: 'Early Shift Started', color: 'primary' },
    leaveRejected: { title: ' Rejected', color: 'error' },
    pendingApproval: { title: 'Pending Approval', color: 'warning' },
    approved: { title: 'Approved', color: 'success' },
    rejected: { title: 'Rejected', color: 'error' },
    pending: { title: 'Pending', color: 'warning' },
    /**
     * Status for sites
     */
    functional: { title: 'Functional', color: 'success' },
    nonFunctional: { title: 'Non Functional', color: 'error' },
    requiresAttention: { title: 'Requires Attention', color: 'error' },
    /**
     * Status for Reports
     */
    accepted: { title: 'Accepted', color: 'success' },
    notSubmitted: { title: 'Not Submitted', color: 'warning' },
    checked_out: { title: 'Checked-out', color: 'error' },
    checked_in: { title: 'Checked-in', color: 'success' },
    loads_checked_out: { title: 'Load-out', color: 'error' },
    loads_checked_in: { title: 'Load-in', color: 'success' },
  };

  return statusEnum[status];
};

export const shiftTypeEnumValue = (type) => {
  const shiftTypeEnum = {
    dedicated: 'Dedicated',
    patrol: 'Patrol',
    hybrid: 'Hybrid',
    extra: 'Extra',
    dispatch: 'Dispatch',
  };
  return shiftTypeEnum[type];
};

export const deepEqual = (obj1, obj2, ignoredKey = 'status') => {
  if (!obj1 || !obj2) {
    return obj1 === obj2;
  }
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }
  const keys1 = Object.getOwnPropertyNames(obj1);
  const keys2 = Object.getOwnPropertyNames(obj2);
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    if (key === ignoredKey) {
      continue;
    }
    const val1 = obj1[key];
    const val2 = obj2[key];
    if (val1 === null || val2 === null) {
      if (val1 !== val2) {
        return false;
      }
    } else if (!keys2.includes(key) || !deepEqual(val1, val2, ignoredKey)) {
      return false;
    }
  }
  return true;
};

export const validateParamForMockApi = (req) => {
  return (
    !req?.params?.id ||
    req.params.id === 'undefined' ||
    req.params.id === 'null' ||
    req.params.id === ':id'
  );
};

export const getCount = (count) => {
  if (!count) return ('0' + 0).slice(-2);
  return ('0' + count).slice(-2);
};

/**
 * remove keys from the object
 */
export const removeKeysFromObject = (object, keysToRemove) => {
  const result = { ...object };
  keysToRemove.forEach((key) => delete result[key]);
  return result;
};

export const setSideBarData = (
  response,
  queryParams,
  setItems,
  setQueryParams,
  setTotalRecords,
  keyVal,
) => {
  if (queryParams?.search && queryParams?.page === 1) {
    setItems(response?.data?.[keyVal]);
  } else if (!queryParams?.search && queryParams?.page === 1) {
    setItems(() => response?.data?.[keyVal]);
  } else {
    setItems((prev) => {
      return [...prev, ...(response?.data?.[keyVal] ?? [])];
    });
  }

  if (response?.pagination) {
    setQueryParams((prev) => ({
      ...prev,
      page: response?.pagination?.currentPage,
    }));

    const total = response?.pagination?.totalCount;

    setTotalRecords(total);
    return;
  }

  if (response?.data?.pagination) {
    setQueryParams((prev) => ({
      ...prev,
      page: response?.data?.pagination?.currentPage,
    }));

    const total = response?.data?.pagination?.totalCount;

    setTotalRecords(total);
  }
};

/**
 * @description helper function to return the place relative name
 * @param {String} placeTypeKey
 */
const findPlaceFromKey = (placeTypeKey) => placesMap[placeTypeKey];

/**
 * @description get location details from placeId
 * @param {String} placeId
 * @returns
 */
export const getPlacesDetail = async (placeId) => {
  try {
    const div = document.createElement('div');
    div.id = 'leads-map-div';
    const map = new google.maps.Map(div, {
      center: { lat: -33.866, lng: 151.196 },
      zoom: 15,
    });
    const request = {
      placeId: placeId,
      fields: [
        'name',
        'formatted_address',
        'place_id',
        'geometry',
        'icon',
        'address_components',
        'adr_address',
        'business_status',
        'formatted_address',
        'geometry',
        'icon',
        'icon_mask_base_uri',
        'icon_background_color',
        'name',
        'photos',
        'place_id',
        'plus_code',
        'type',
        'url',
        'vicinity',
        'wheelchair_accessible_entrance',
        'formatted_phone_number',
        'international_phone_number',
        'website',
      ],
    };
    const service = new google.maps.places.PlacesService(map);
    return new Promise((resolve, reject) => {
      service.getDetails(request, (place, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location
        ) {
          let res = place;
          if (place?.types?.length) {
            res.type = findPlaceFromKey(place?.types?.[0]);
          }
          resolve(res);
        } else {
          reject('No place found');
        }
      });
    });
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};

export const formatNumberToKMBT = (number) => {
  const suffixes = ['', 'K', 'M', 'B', 'T'];

  if (number >= 10000) {
    let suffixIndex = 0;
    while (number >= 1000 && suffixIndex < suffixes.length - 1) {
      number /= 1000;
      suffixIndex++;
    }

    return number?.toFixed(1) + suffixes[suffixIndex];
  } else {
    return number?.toString();
  }
};

export const convertFileSize = (sizeInBytes) => {
  if (typeof sizeInBytes !== 'number' || isNaN(sizeInBytes)) {
    return 'Invalid size';
  }

  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  let i = 0;
  while (sizeInBytes >= 1024 && i < units.length - 1) {
    sizeInBytes /= 1024;
    i++;
  }

  return sizeInBytes.toFixed(2) + ' ' + units[i];
};

export const truncateString = (str, maxLength = 75) => {
  if (str.length <= maxLength) {
    return str; // No truncation needed if string is shorter or equal to maxLength
  }

  const omissionLength = 3; // Length of the ellipsis (...)
  const firstChars = Math.min(10, Math.floor(maxLength / 2)); // Ensure at least 10 first characters
  const lastChars = Math.min(10, Math.floor(maxLength / 2)); // Ensure at least 10 last characters
  const middleLength = maxLength - firstChars - lastChars - omissionLength;

  const midpoint = Math.floor(str.length / 2);
  const truncatedStart = str.slice(0, midpoint - Math.floor(middleLength / 2));
  const truncatedEnd = str.slice(midpoint + Math.ceil(middleLength / 2));

  return `${truncatedStart}...${truncatedEnd}`;
};

export const isSameDate = (startDate, endDate) => {
  return startDate?.isSame(endDate, 'day');
};

export const delay = async (delay) => await new Promise((resolve) => setTimeout(resolve, delay)); // 3 sec

export const checkIfTimeIsPassedAlready = (date, dayjsDateTime) => {
  // Check if the selected date is today. If true, then check if the selected time is in the past.
  return (
    dayjs(date).isSame(dayjs(), 'day') && // Check if the start date is today
    dayjs(dayjsDateTime).isBefore(dayjs(), 'minute')
  );
};

export const checkIfTimeIsSame = (startTime, endTime) => {
  return dayjs(startTime).format('HH:mm:ss') === dayjs(endTime).format('HH:mm:ss');
};

export const selectedDatesToUTC = (selectedDates = []) => {
  return selectedDates?.map((date) => date?.utc()?.format());
};

export const startAndEndDateFilterWithUTCTime = (selectedDates = []) => {
  // return selectedDates?.map(date => {
  //   if ()
  // })
  //     ? params?.selectedDates?.[0]?.set('hour', 23).set('minute', 59)?.utc()?.toISOString()
  return [
    selectedDates?.[0].set('hour', 0).set('minute', 0)?.utc()?.toISOString(),
    selectedDates?.[1]?.set('hour', 24).set('minute', 0)?.utc()?.toISOString(),
  ];
};

export const generateAzureStorageBlobToken = async (body) => {
  try {
    const accessToken = await getUploadGenerationToken(body);
    return accessToken?.data;
  } catch (error) {
    console.error(' Error :: Azure Storage Token: ' + JSON.stringify(error));
    throw error;
  }
};

export const uploadAssetsToAzureStorageBlob = async ({
  payload,
  uploadProgressCallBack,
  containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME,
  saasToken = undefined,
  _t,
  cancelToken,
}) => {
  // if (payload.type === 'image/gif') {
  //   let fileExtension = payload.type.split('/');
  //
  //   // Alert.alert(
  //   //   t('global.invalidContentType'),
  //   //   `${t('global.unsupportedFileFormat')}: .${fileExtension[1].toUpperCase()}`,
  //   //   [{ text: 'OK' }],
  //   // );
  //   return null;
  // }
  const baseURL = process.env.REACT_APP_AZURE_ACCOUNT_URL;

  const accessToken = saasToken
    ? saasToken
    : await generateAzureStorageBlobToken({
        permissions: ['w', 'r', 'd'],
      });
  const timestamp = new Date().getTime();
  const date = new Date().getDate();
  const hours = new Date().getHours();
  let fileName;

  let extensionGet = /(?:\.([^.]+))?$/;

  if (payload.fileName) {
    let extension = extensionGet.exec(payload.fileName)[1];
    fileName = `${timestamp}-${date}-${hours}_${generateUniqueId()}.${extension}`;
  } else if (payload.name) {
    let extension = extensionGet.exec(payload.name)[1];
    fileName = `${timestamp}-${date}-${hours}_${generateUniqueId()}.${extension}`;
  } else {
    fileName = `${timestamp}_${generateUniqueId()}.png`;
  }

  fileName = fileName.trim();
  const endPoint = `${baseURL}/${containerName}/${fileName}?${accessToken}`;
  let buffer = null;

  if (isObject(payload)) {
    buffer = await convertFileToBuffer(payload);
  } else {
    const base64Data = payload.replace(/^data:image\/\w+;base64,/, '');
    buffer = Buffer.from(base64Data, 'base64');
  }

  let config = {
    method: 'put',
    url: endPoint,
    data: buffer,
    headers: {
      'x-ms-blob-type': 'BlockBlob',
    },
    cancelToken,
    onUploadProgress: (progressEvent) => {
      let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

      // You can use the progress information as needed (e.g., update UI)

      uploadProgressCallBack?.(percentCompleted);
    },
  };

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    try {
      const res = await axios.request(config);
      let sizeInBytes = 0;

      if (!isObject(payload)) {
        const base64Content = payload.split(',')[1] || payload;

        // Decode base64 string
        const binaryString = atob(base64Content);

        // Calculate file size in bytes
        const byteArray = new Uint8Array([...binaryString].map((char) => char.charCodeAt(0)));

        sizeInBytes = byteArray.length;
      }

      if (res.status === 201) {
        resolve({
          key: fileName,
          contentType:
            payload.type === 'image/jpg' ? 'image/jpeg' : payload.type ? payload.type : 'image/png',
          byteSize: payload.fileSize ? payload.fileSize : payload.size ? payload.size : sizeInBytes,
          filename: fileName,
          imgUrl: endPoint,
        });
      } else {
        reject('Upload failed');
      }
    } catch (error) {
      reject(error);
    }
  });
};

export const uploadAllAssetsToAzureStorageBlob = async ({
  assets,
  uploadProgressCallBack,
  cancelTokens = [],
  presetToken,
}) => {
  try {
    const saasToken = presetToken
      ? presetToken
      : await generateAzureStorageBlobToken({
          permissions: ['w', 'r', 'd'],
        });
    const results = await Promise.allSettled(
      assets.map((asset, _index) => {
        if (!asset?.alreadyUploaded) {
          const source = axios.CancelToken.source();
          cancelTokens?.push(source);
          return uploadAssetsToAzureStorageBlob({
            payload: asset,
            saasToken,
            uploadProgressCallBack,
            cancelToken: source.token,
          });
        } else {
          return asset;
        }
      }),
    );
    let successfullyUploaded = [];
    let needsReupload = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        // Handle successful upload (result.value contains the result)
        console.log(`Asset ${index} uploaded successfully:`, result.value);

        successfullyUploaded.push(result.value);
      } else {
        console.error(`Asset ${index} upload failed:`, result.reason);
        console.error(`Asset ${index} upload failed:`, assets[index]);
        needsReupload.push(assets[index]);
      }
    });
    return {
      successfullyUploaded: successfullyUploaded,
      needsReupload: needsReupload,
    };
  } catch (error) {
    console.error('Error uploading assets:', error);
  }
};

export const isObject = (value) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const convertFileToBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

export const convertBase64ToBuffer = (base64) => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const generateInitialValues = (sectionsAttributes) => {
  let initialValues = {};

  sectionsAttributes?.forEach((section, _sectionIndex) => {
    section?.questionsAttributes?.forEach((question, _questionIndex) => {
      initialValues[question.id] = question?.answers ?? '';
      if (
        question?.responseType === enumResponseType.multiselect ||
        question?.responseType === enumResponseType.dropdown ||
        question?.responseType === enumResponseType.attachments ||
        question?.responseType === enumResponseType.imageVideo
      ) {
        initialValues[question.id] = question?.answers ?? [];
      }
    });
  });

  return { initialValues };
};

export const removeNotRequiredKeys = (data, template) => {
  let dataToBeValidated = {};

  template?.sectionsAttributes.forEach((section) => {
    section?.questionsAttributes?.forEach((question) => {
      if (question.required) {
        dataToBeValidated[question.id] = data[question.id];
      }
    });
  });

  return dataToBeValidated;
};

export const generateURLFromObject = (
  obj,
  accessToken,
  containerName = process.env.REACT_APP_AZURE_CONTAINER_NAME,
) => {
  const baseURL = process.env.REACT_APP_AZURE_ACCOUNT_URL;

  return `${baseURL}/${containerName}/${obj?.filename}?${accessToken}`;
};

export const assignTheAnswers = async ({
  value,
  reportData,
  setUploadProgress,
  setIsUploading,
  setFailedToUpload,
  saasToken = null,
}) => {
  let copyData = { ...reportData }; // Shallow copy
  let totalFiles = 0;
  let completedFiles = 0;
  setUploadProgress(0);

  // eslint-disable-next-line no-unsafe-optional-chaining
  for (const [_sectionIndex, section] of copyData?.sectionsAttributes?.entries()) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    for (const [_questionIndex, question] of section?.questionsAttributes?.entries()) {
      const fieldName = question?.id;

      if (
        question.responseType == enumResponseType.imageVideo ||
        question.responseType == enumResponseType.webCam ||
        question.responseType == enumResponseType.attachments
      ) {
        if (value[fieldName] && value[fieldName]?.length > 0) {
          totalFiles += value[fieldName].length;
        }
      }
    }
  }

  setIsUploading(true);
  // eslint-disable-next-line no-unsafe-optional-chaining
  for (const [sectionIndex, section] of copyData?.sectionsAttributes?.entries()) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    for (const [_questionIndex, question] of section?.questionsAttributes?.entries()) {
      const fieldName = question?.id;

      if (
        question.responseType == enumResponseType.imageVideo ||
        question.responseType == enumResponseType.webCam ||
        question.responseType == enumResponseType.attachments ||
        question.responseType == enumResponseType.signature
      ) {
        if (value[fieldName] && value[fieldName]?.length > 0) {
          const result = await uploadAllAssetsToAzureStorageBlob({
            assets:
              question.responseType == enumResponseType.webCam ||
              question.responseType == enumResponseType.signature
                ? value[fieldName]?.[0]?.alreadyUploaded &&
                  question.responseType == enumResponseType.signature
                  ? value[fieldName]
                  : [value[fieldName]]
                : value[fieldName],
            uploadProgressCallBack: (progress) => {
              if (progress === 100) {
                ++completedFiles;
                setUploadProgress(Math.floor(((completedFiles * 100) / (totalFiles * 100)) * 100));
              } else {
                if (completedFiles > 0) {
                  setUploadProgress(
                    Math.floor(((completedFiles * 100 + progress) / (totalFiles * 100)) * 100),
                  );
                } else {
                  setUploadProgress(Math.floor((progress / (totalFiles * 100)) * 100));
                }
              }
            },
            cancelTokens: [],
            presetToken: saasToken,
          });
          if (result.needsReupload.length > 0) {
            let failed = [];
            result?.needsReupload?.map((e) => {
              failed.push({ sectionIndex, ...e });
            });
            setFailedToUpload((prev) => [...prev, ...failed]);
          }

          if (question.responseType == enumResponseType.webCam) {
            question.answers = result.successfullyUploaded[0];
          } else {
            question.answers = result.successfullyUploaded;
          }
        } else {
          question.answers = value[fieldName];
        }
      } else {
        question.answers = value[fieldName];
      }
    }
  }

  return copyData;
};

export const checkDLSWrtDate = (date) => {
  // if date is undefined, it means we are picking current date and time
  return {
    isDLSWrtDate: getOffsetWithStandardTime() !== dayjsWithTimezone(date).utcOffset(),
    offsetDiff: getOffsetWithStandardTime() - dayjsWithTimezone(date).utcOffset(), // offsetDiff will be negative if DLS is enabled and current timezone is at negative offset and vice versa
  };
};

export const adjustHourForTimePayloadInIso = (dateTime) => {
  const { offsetDiff: offsetDiffWRTCurrentDate } = checkDLSWrtDate();
  const dateTimeInIso = dayjs(dateTime)?.toISOString();
  return dayjs(dateTimeInIso).add(-offsetDiffWRTCurrentDate, 'minute');
};
export const getCurrentStandardTimeInIsoWrtTimezone = () => adjustHourForTimePayloadInIso();

export const checkACLPermission = (aclPermissions, routePermission) => {
  try {
    let hasPermission = null;
    const paths = routePermission.split('.');

    // Traverse the permissions object
    let currentLevel = aclPermissions;
    for (const path of paths) {
      currentLevel = currentLevel?.[path];

      // If we can't find the next level, return false
      if (currentLevel === undefined) {
        hasPermission = false;
      }
    }

    if (currentLevel) {
      hasPermission = true;
    }

    // If the final level is an object with CRUD actions
    if (typeof currentLevel === 'object') {
      hasPermission = true;
    }
    return hasPermission;
  } catch (e) {
    console.error({ e: e.message, aclPermissions, routePermission });
  }
};
